import { z } from "zod";

// ---- CT-RM-26: card updater, expiring cards and failed-payment recovery (dunning) ----

/**
 * Dunning state of a recurring plan, alongside its lifecycle `status`:
 * - active: payments are healthy
 * - past_due: a charge failed; the first retry is scheduled
 * - recovering: at least one retry failed (or the card was just updated) and more retries remain
 * - recovered: a retry succeeded; back to active after the next regular charge
 * - lapsed: the retry schedule is exhausted; the plan stops charging until staff step in
 */
export const DUNNING_STATES = ["active", "past_due", "recovering", "recovered", "lapsed"] as const;
export type DunningState = (typeof DUNNING_STATES)[number];
export const DUNNING_STATE_LABELS: Record<DunningState, string> = {
  active: "Active",
  past_due: "Past due",
  recovering: "Recovering",
  recovered: "Recovered",
  lapsed: "Lapsed",
};
/** states in which a scheduled retry is pending */
export const DUNNING_RETRYING_STATES: readonly DunningState[] = ["past_due", "recovering"];

/** Smart-retry default: retry 1, 3, 5 and 7 days after the failed charge. */
export const RECURRING_DEFAULT_RETRY_DAYS = [1, 3, 5, 7] as const;
export const RECURRING_MAX_RETRIES = 8;
export const RECURRING_MAX_RETRY_DAY = 60;
export const RECURRING_DEFAULT_LINK_TTL_HOURS = 168;

export const zRetryDays = z
  .array(z.number().int().min(1).max(RECURRING_MAX_RETRY_DAY))
  .min(1)
  .max(RECURRING_MAX_RETRIES)
  .refine((days) => days.every((d, i) => i === 0 || d > days[i - 1]!), { message: "Retry days must be in ascending order with no repeats" });

export const zRecurringSettings = z.object({
  retryDays: zRetryDays,
  updateLinkTtlHours: z.number().int().min(1).max(720),
  taskAssigneeId: z.string().uuid().nullable(),
});
export type RecurringSettings = z.infer<typeof zRecurringSettings>;
export const zUpdateRecurringSettings = zRecurringSettings.partial();

/** Stored retry days, falling back to the default when missing or malformed. */
export function normalizeRetryDays(raw: unknown): number[] {
  const parsed = zRetryDays.safeParse(raw);
  return parsed.success ? parsed.data : [...RECURRING_DEFAULT_RETRY_DAYS];
}

const addDaysIso = (isoDate: string, days: number) => new Date(Date.parse(`${isoDate}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);

/** The plan fields the dunning machine reads and writes. */
export interface DunningPlan {
  dunningState: DunningState;
  retryAttempt: number;
  pastDueSince: string | null;
  nextRetryDate: string | null;
}
export type DunningEventKind = "payment_failed" | "retry_failed" | "lapsed" | "recovered";
export interface DunningTransition {
  next: DunningPlan;
  /** ledger event the transition records (null when nothing notable happened) */
  event: DunningEventKind | null;
}

/**
 * A charge (or retry) failed on `today`. A healthy plan opens a new dunning cycle (past_due, first
 * retry at failure + retryDays[0]); a plan already in the cycle advances to the next retry day
 * (recovering) or, once the schedule is exhausted, lapses.
 */
export function dunningOnFailure(plan: DunningPlan, today: string, retryDays: readonly number[]): DunningTransition {
  if (plan.dunningState === "lapsed") return { next: plan, event: null };
  if (plan.dunningState === "active" || plan.dunningState === "recovered") {
    if (!retryDays.length) return { next: { dunningState: "lapsed", retryAttempt: 0, pastDueSince: today, nextRetryDate: null }, event: "lapsed" };
    return { next: { dunningState: "past_due", retryAttempt: 0, pastDueSince: today, nextRetryDate: addDaysIso(today, retryDays[0]!) }, event: "payment_failed" };
  }
  const attempt = plan.retryAttempt + 1;
  const since = plan.pastDueSince ?? today;
  if (attempt >= retryDays.length) {
    return { next: { dunningState: "lapsed", retryAttempt: attempt, pastDueSince: since, nextRetryDate: null }, event: "lapsed" };
  }
  let nextRetry = addDaysIso(since, retryDays[attempt]!);
  if (nextRetry <= today) nextRetry = addDaysIso(today, 1); // a late retry never schedules the next one in the past
  return { next: { dunningState: "recovering", retryAttempt: attempt, pastDueSince: since, nextRetryDate: nextRetry }, event: "retry_failed" };
}

/** A charge succeeded: a plan in the dunning cycle is recovered; a recovered plan's next regular charge returns it to active. */
export function dunningOnSuccess(plan: DunningPlan): DunningTransition {
  const cleared = { retryAttempt: 0, pastDueSince: null, nextRetryDate: null };
  if (plan.dunningState === "past_due" || plan.dunningState === "recovering" || plan.dunningState === "lapsed") {
    return { next: { dunningState: "recovered", ...cleared }, event: "recovered" };
  }
  return { next: { dunningState: "active", ...cleared }, event: null };
}

/** The card changed while a retry is pending: retry at the next daily run instead of waiting for the schedule. */
export function dunningOnCardUpdated(plan: DunningPlan, today: string): DunningPlan {
  if (plan.dunningState !== "past_due" && plan.dunningState !== "recovering") return plan;
  return { ...plan, dunningState: "recovering", nextRetryDate: today };
}

/** Last day a card is valid ("12/26" is good through 2026-12-31). */
export function cardValidThrough(expMonth: number, expYear: number): string {
  return new Date(Date.UTC(expYear, expMonth, 0)).toISOString().slice(0, 10);
}

/** Does a card with this expiry run out before `chargeDate`? */
export function cardExpiresBefore(expMonth: number | null, expYear: number | null, chargeDate: string | null): boolean {
  if (!expMonth || !expYear || !chargeDate) return false;
  return cardValidThrough(expMonth, expYear) < chargeDate;
}

/** "YYYY-MM": identifies one card expiry, so a flag is raised once per card. */
export const cardExpiryKey = (expMonth: number, expYear: number) => `${expYear}-${String(expMonth).padStart(2, "0")}`;

/** "Visa •••• 4242, exp 12/26" — the only form a card is ever shown in. */
export function describeCard(card: { cardBrand: string | null; cardLast4: string | null; cardExpMonth: number | null; cardExpYear: number | null }): string {
  if (!card.cardLast4) return "No card on file";
  const brand = card.cardBrand ? card.cardBrand[0]!.toUpperCase() + card.cardBrand.slice(1) : "Card";
  const exp = card.cardExpMonth && card.cardExpYear ? `, exp ${String(card.cardExpMonth).padStart(2, "0")}/${String(card.cardExpYear).slice(-2)}` : "";
  return `${brand} •••• ${card.cardLast4}${exp}`;
}

export const RECURRING_ATTENTION_REASONS = ["card_expiring", "past_due", "recovering", "lapsed"] as const;
export type RecurringAttentionReason = (typeof RECURRING_ATTENTION_REASONS)[number];

export interface RecurringAttentionItem {
  id: string;
  constituentId: string;
  constituentName: string;
  amount: number;
  frequency: string;
  status: string;
  dunningState: DunningState;
  reason: RecurringAttentionReason;
  paymentProvider: string | null;
  cardBrand: string | null;
  cardLast4: string | null;
  cardExpMonth: number | null;
  cardExpYear: number | null;
  nextChargeDate: string | null;
  nextRetryDate: string | null;
  retryAttempt: number;
  retryCount: number;
  pastDueSince: string | null;
  lastFailureReason: string | null;
  cardExpiryFlaggedAt: string | null;
}

export interface RecurringRecoveryMetrics {
  /** money brought back by successful retries in the last 90 days, in cents */
  recoveredAmount90d: number;
  recoveredCount90d: number;
  recoveredAmountAllTime: number;
  /** per-charge amount of plans currently past due or recovering */
  atRiskAmount: number;
  atRiskCount: number;
  needsAttention: number;
}

export interface CardUpdateLink {
  /** shown once; only its hash is stored */
  url: string;
  expiresAt: string;
}

/** What the donor's update-card page shows (no card data beyond brand/last4/expiry). */
export interface PublicCardUpdatePage {
  organizationName: string;
  amount: number;
  frequency: string;
  currency: string;
  card: string;
  /** hosted = the provider's card-update page; stub = no payment provider is configured (test/dev) */
  mode: "hosted" | "stub";
}

export type CardUpdateSession = { mode: "hosted"; url: string } | { mode: "stub"; message: string };

export interface RecurringSweepResult {
  flagged: number;
  tasks: number;
  retried: number;
  recovered: number;
  lapsed: number;
}
